import React, { useState } from "react";
import { Shield, ChevronDown, ChevronRight } from "lucide-react";
import useFireboxLive, { saKey, wib, ageMin, rates, mbps, bytes, uptimeText } from "./useFireboxLive";

// Jakarta office WatchGuard T80 (NFPIMIDJKTKN01) — BOVPN + AWS tunnel map, as seen from the Jakarta side.
// Each site has its own WatchGuard; this panel only shows tunnels terminated on the Jakarta Firebox.
// Sources (2026-10-01): Fireware Web UI (VPN Statistics, Interfaces, Routes, Policies),
// WSM Firebox System Manager, SNMP (WATCHGUARD IPSEC MIB 3097.6.5) via probe agent 10.40.1.58.
// Live: tunnel active flags come from GET /api/net/firebox/status (SNMP every 5 min); static data is the fallback.
const SNAPSHOT = "2026-10-01 16:50 WIB";
const FIREBOX = { name: "NFPIMIDJKTKN01", ver: "Fireware 12.12.2", lan: "10.32.0.17" };

const IFACES = [
  ["eth0", "Velo", "External", "222.165.199.102/30", "Primary internet + site BOVPN", "174 GB / 860 GB"],
  ["eth2", "Neuviz", "External", "10.26.4.178/30", "Secondary internet", "233 GB / 285 GB"],
  ["eth3", "AWS-JKT TUNNEL", "External", "222.165.224.125/29", "AWS Jakarta Site-to-Site VPN", "1.3 GB / 0.98 GB"],
  ["eth5", "to coreswitch", "Trusted", "10.32.0.17/23", "Jakarta office LAN", "1.3 TB / 370 GB"],
  ["eth6", "AWS Tunnel", "External", "192.168.10.2/24", "Tunnel to AWS Singapore via Velo MikroTik 192.168.10.1", "2.8 GB / 20.7 GB"],
  ["eth7", "SAP HOST TESTING", "External", "222.165.224.124/29", "-", "2.0 GB / 1.8 GB"],
];

// tunnels: [label, local, remote, active(true|false|null=unknown), note]
const GATEWAYS = [
  { key: "aws", name: "AWS Jakarta VPC", iface: "eth3", sub: "Indomas account · vpn-0c9c746c9c3b0879f", status: "up",
    peers: ["16.78.66.179 (bvpn1, metric 1)", "108.136.96.201 (bvpn2, metric 2)"],
    note: "Route-based (VIF) VPN, route 10.40.0.0/23 (EC2 10.40.1.58). Observed 1 Oct: outbound via bvpn1, inbound via bvpn2. Availability: see the health table (AWS CloudWatch).",
    tunnels: [
      ["bvpn1 VIF", "0.0.0.0/0", "10.40.0.0/23", true, "Sent 1.1 GB / Recv 0, 60 rekeys"],
      ["bvpn2 VIF", "0.0.0.0/0", "10.40.0.0/23", true, "Sent 5 MB / Recv 775 MB, 57 rekeys"],
    ] },
  { key: "sg", name: "AWS Singapore (PIL)", iface: "eth6", sub: "PIL account · VPN-Jakarta Velo, terminated on the Velo MikroTik", status: "unknown",
    peers: ["192.168.10.1 (Velo MikroTik)"],
    note: "Not a Firebox BOVPN, so it is not in Firebox SNMP. It IS monitored from the AWS side: health table → 'AWS Singapore (PIL) ⇄ Jakarta office' (CloudWatch, customer gateway 222.165.224.122). Steered by policies 'AWS Tunneling Out/In' (default route via 192.168.10.1, metric 20). Dashboard (10.32.18.160) reaches Firebox at 192.168.10.2 in ~17 ms. Velo also carries eth0 — single provider for internet and the Singapore tunnel.",
    tunnels: [
      ["", "10.32.0.0/23 + site subnets", "10.32.18.0/23, 192.168.10.0/24, 10.32.19.97", null, "Not visible to the Firebox; status from AWS CloudWatch in the health table"],
    ] },
  { key: "dumai", name: "PIM To DUMAI", sub: "Dumai site · Indopalm", status: "up", peers: ["103.94.126.130"],
    tunnels: [
      ["", "10.32.0.0/23", "10.32.30.0/23", true, "2.1 GB / 1.67 GB"],
      ["", "10.32.0.0/23", "10.32.8.0/24", true, "idle"],
      ["", "10.32.0.0/23", "10.32.9.10/32", true, "7 MB / 156 MB"],
      ["", "10.32.0.0/23", "10.43.0.0/16", true, "122 MB / 241 MB"],
      ["", "10.32.4.0/23", "10.43.0.0/16", true, "3 MB / 3 MB"],
      ["", "10.40.0.0/23", "10.32.30.0/23", true, "AWS Jakarta transit"],
      ["", "10.40.0.0/23", "10.43.0.0/16", true, "AWS Jakarta transit"],
      ["", "10.32.18.0/23", "10.32.5.0/26", false, ""],
    ] },
  { key: "kijing", name: "PIM To KIJING", sub: "Kijing site · Bio", status: "warn",
    peers: ["36.95.179.155 (endpoint 2, active)", "58.147.187.26 (endpoint 1, ERROR)"],
    note: "Endpoint 1: Message retry timeout. Running on backup endpoint 2. Dropped from 4 to 2 active tunnels between 15:30 and 16:50.",
    tunnels: [
      ["", "10.32.0.0/23", "10.38.0.0/22", true, "42 MB / 36 MB"],
      ["", "10.32.19.97/32", "10.38.0.0/23", true, "Singapore host via Firebox"],
      ["", "10.32.0.0/23", "10.35.45.0/25", false, "Dropped after 15:30"],
      ["", "10.32.19.97/32", "10.35.50.0/23", false, "Dropped after 15:30"],
      ["", "10.32.0.0/23", "10.40.30.12/32", false, ""],
      ["", "10.32.18.0/23", "10.20.10.0/24", false, ""],
      ["", "10.32.18.0/23", "10.31.12.0/24", false, ""],
      ["", "10.32.18.20/32", "10.35.50.0/23", false, ""],
      ["", "10.32.18.20/32", "10.38.0.0/22", false, ""],
      ["", "10.40.0.0/23", "10.38.0.0/23", false, ""],
    ] },
  { key: "medan", name: "PIM To MEDAN", sub: "Medan site · PAMIN, Palmindo, Oleochem", status: "up", peers: ["202.162.195.163"],
    tunnels: [
      ["", "10.32.0.0/23", "10.21.1.4/32", true, ""],
      ["", "10.32.0.0/23", "10.26.0.0/16", true, ""],
      ["", "10.32.0.0/23", "10.32.10.0/24", true, ""],
      ["", "10.32.0.0/23", "10.32.22.0/23", true, ""],
      ["", "10.32.0.0/23", "10.32.24.0/23", true, ""],
      ["", "10.32.0.0/23", "10.32.26.0/23", true, ""],
      ["", "10.32.0.0/23", "10.32.4.0/24", false, ""],
      ["", "10.40.0.0/23", "10.26.0.0/16", false, ""],
    ] },
];

const COLOR = { up: "#16a34a", warn: "#d97706", down: "#dc2626", unknown: "#64748b" };
const LABEL = { up: "Up", warn: "Degraded", down: "Down", unknown: "Not on Firebox" };
const card = { background: "var(--card-bg, #fff)", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16 };
const chip = (c) => ({ display: "inline-block", padding: "1px 8px", borderRadius: 999, fontSize: 11, fontWeight: 600, color: c, background: c + "1a", border: `1px solid ${c}55`, whiteSpace: "nowrap" });
const th = { padding: 4, textAlign: "left", color: "#64748b", fontWeight: 600 };
const td = { padding: 4 };
const mono = { padding: 4, fontFamily: "monospace" };
const counted = (g) => g.tunnels.filter((t) => t[3] !== null);
const PEER_GW = { "103.94.126.130": "dumai", "36.95.179.155": "kijing", "58.147.187.26": "kijing", "202.162.195.163": "medan", "16.78.66.179": "aws", "108.136.96.201": "aws" };
// Overlay live SNMP SAs on the static tunnel list (match by local/remote network, AWS by peer).
function applyLive(gws, live) {
  if (!live || !live.sas) return gws;
  const keys = new Set(live.sas.map((x) => saKey(x.local, x.remote)));
  const peers = {};
  live.sas.forEach((x) => { const g = PEER_GW[x.peer]; if (g) (peers[g] = peers[g] || new Set()).add(x.peer); });
  return gws.map((g) => {
    if (g.key === "sg") return g;
    let tunnels;
    if (g.key === "aws") tunnels = g.tunnels.map((t, i) => { const p = i === 0 ? "16.78.66.179" : "108.136.96.201"; const sa = live.sas.find((x) => x.peer === p); return [t[0], t[1], t[2], !!sa, sa ? `${sa.rekeys} rekeys (live)` : "no SA (live)"]; });
    else tunnels = g.tunnels.map((t) => [t[0], t[1], t[2], keys.has(saKey(t[1], t[2])), ""]);
    const act = tunnels.filter((t) => t[3]).length;
    let status = act === 0 ? "down" : "up";
    let note = g.note;
    if (g.key === "kijing" && act) {
      const pk = peers.kijing || new Set();
      if (pk.has("36.95.179.155") && !pk.has("58.147.187.26")) { status = "warn"; note = "Running on backup endpoint 2 (36.95.179.155); endpoint 1 (58.147.187.26) has no SA."; }
      else note = "Endpoint 1 (58.147.187.26) active.";
    }
    if (g.key === "aws" && act === 1) status = "warn";
    return { ...g, tunnels, status, note };
  });
}

function Diagram({ gws }) {
  const W = 780, H = 320, cx = 390, cy = 160;
  const pos = { aws: [115, 80], sg: [115, 240], dumai: [665, 60], kijing: [665, 160], medan: [665, 260] };
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", maxWidth: W, height: "auto", display: "block" }} role="img" aria-label="Jakarta WatchGuard tunnel topology">
      {gws.map((g) => {
        const [x, y] = pos[g.key]; const c = COLOR[g.status];
        const n = counted(g); const act = n.filter((t) => t[3]).length;
        const lbl = g.key === "sg" ? "eth6 · Velo tunnel" : `${g.iface ? g.iface + " · " : "eth0 · "}${act}/${n.length} SA`;
        return (
          <g key={g.key}>
            <line x1={cx} y1={cy} x2={x} y2={y} stroke={c} strokeWidth={2.5} strokeDasharray={g.status === "up" ? "" : "6 4"} />
            <text x={x + (cx - x) * 0.42} y={y + (cy - y) * 0.42 - 6} textAnchor="middle" fontSize="11" fill="#64748b">{lbl}</text>
          </g>
        );
      })}
      <rect x={cx - 100} y={cy - 40} width={200} height={80} rx={10} fill="rgb(var(--brand-600, 13 148 136))" />
      <text x={cx} y={cy - 16} textAnchor="middle" fontSize="13" fontWeight="700" fill="#fff">WatchGuard Jakarta (T80)</text>
      <text x={cx} y={cy + 2} textAnchor="middle" fontSize="11" fill="#e6fffa">Indomas · LAN {FIREBOX.lan}</text>
      <text x={cx} y={cy + 18} textAnchor="middle" fontSize="10" fill="#e6fffa">10.32.0.0/23 · 10.32.4.0/23</text>
      {gws.map((g) => {
        const [x, y] = pos[g.key]; const c = COLOR[g.status];
        return (
          <g key={g.key + "n"}>
            <rect x={x - 105} y={y - 27} width={210} height={54} rx={8} fill="#fff" stroke={c} strokeWidth={2} />
            <circle cx={x - 90} cy={y - 10} r={5} fill={c} />
            <text x={x - 80} y={y - 6} fontSize="12" fontWeight="700" fill="#0f172a">{g.name}</text>
            <text x={x - 92} y={y + 10} fontSize="10" fill="#64748b">{g.sub.length > 38 ? g.sub.slice(0, 37) + "…" : g.sub}</text>
            <text x={x - 92} y={y + 22} fontSize="10" fill="#94a3b8">{g.peers[0].split(" ")[0]}{g.peers.length > 1 ? ` +${g.peers.length - 1}` : ""}</text>
          </g>
        );
      })}
    </svg>
  );
}

function GatewayBlock({ g }) {
  const [open, setOpen] = useState(g.status !== "up");
  const n = counted(g); const act = n.filter((t) => t[3]).length;
  return (
    <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, marginBottom: 8, overflow: "hidden" }}>
      <button onClick={() => setOpen(!open)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", background: "transparent", border: 0, cursor: "pointer", textAlign: "left", flexWrap: "wrap" }}>
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <span style={{ fontWeight: 700 }}>{g.name}</span>
        <span style={{ fontSize: 12, color: "#64748b" }}>{g.sub}</span>
        <span style={chip(COLOR[g.status])}>{LABEL[g.status]}</span>
        <span style={{ marginLeft: "auto", fontSize: 12, color: "#64748b" }}>{n.length ? `${act} active / ${n.length} configured` : "via " + g.iface}</span>
      </button>
      {open && (
        <div style={{ padding: "0 12px 12px" }}>
          <div style={{ fontSize: 12, color: "#475569", marginBottom: 6 }}>Peer: {g.peers.join(" · ")}{g.iface ? ` · interface ${g.iface}` : ""}</div>
          {g.note && <div style={{ fontSize: 12, color: g.status === "warn" ? COLOR.warn : "#475569", marginBottom: 8 }}>{g.note}</div>}
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead><tr><th style={th}>Local</th><th style={th}>Remote</th><th style={th}>Status</th><th style={th}>Traffic / note</th></tr></thead>
              <tbody>
                {g.tunnels.map((t, i) => (
                  <tr key={i} style={{ borderTop: "1px solid #f1f5f9" }}>
                    <td style={mono}>{t[0] ? t[0] + " " : ""}{t[1]}</td>
                    <td style={mono}>{t[2]}</td>
                    <td style={td}><span style={chip(t[3] === null ? COLOR.unknown : t[3] ? COLOR.up : "#94a3b8")}>{t[3] === null ? "Unknown" : t[3] ? "SA up" : "Idle (no SA)"}</span></td>
                    <td style={{ ...td, color: "#64748b" }}>{t[4]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Interfaces() {
  const [open, setOpen] = useState(false);
  const { live, prev } = useFireboxLive();
  const RT = rates(live, prev);
  const ROWS = IFACES.map((r) => {
    const n = Number(String(r[0]).replace(/\D/g, ""));
    const L = live && live.ifs && live.ifs.find((x) => x.eth === n);
    if (!L || L.in == null) return r;
    const R = RT[n];
    const q = r.slice();
    q[5] = bytes(L.out) + " / " + bytes(L.in) + (R && R.inMbps != null ? " · now ↑" + mbps(R.outMbps) + " ↓" + mbps(R.inMbps) : "") + (L.oper === "down" ? " · DOWN" : "");
    return q;
  });
  const up = live && live.uptime != null ? uptimeText(live.uptime) : null;
  return (
    <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, marginBottom: 8, overflow: "hidden" }}>
      <button onClick={() => setOpen(!open)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", background: "transparent", border: 0, cursor: "pointer", textAlign: "left" }}>
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <span style={{ fontWeight: 700 }}>Firebox interfaces</span>
        <span style={{ fontSize: 12, color: "#64748b" }}>sent / received since boot ({up ? up + ", live" : "18 days, snapshot"})</span>
      </button>
      {open && (
        <div style={{ padding: "0 12px 12px", overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead><tr><th style={th}>Port</th><th style={th}>Name</th><th style={th}>Type</th><th style={th}>IPv4</th><th style={th}>Role</th><th style={th}>Sent / Received</th></tr></thead>
            <tbody>
              {ROWS.map((r) => (
                <tr key={r[0]} style={{ borderTop: "1px solid #f1f5f9" }}>
                  <td style={mono}>{r[0]}</td><td style={td}>{r[1]}</td><td style={td}>{r[2]}</td><td style={mono}>{r[3]}</td><td style={{ ...td, color: "#64748b" }}>{r[4]}</td><td style={td}>{r[5]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function BovpnJakartaMap() {
  const { live, checkedAt, error } = useFireboxLive();
  const G = applyLive(GATEWAYS, live);
  const bo = G.filter((g) => g.key !== "sg");
  const total = bo.reduce((a, g) => a + counted(g).length, 0);
  const active = bo.reduce((a, g) => a + counted(g).filter((t) => t[3]).length, 0);
  return (
    <div style={card}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <Shield size={20} color="rgb(var(--brand-600, 13 148 136))" />
        <div style={{ marginRight: "auto" }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Jakarta WatchGuard — BOVPN &amp; AWS tunnels</div>
          <div style={{ fontSize: 12, color: "#64748b" }}>{FIREBOX.name} · {FIREBOX.ver} · seen from the Jakarta side (each site has its own WatchGuard) · {live ? <>live SNMP · checked {wib(checkedAt)}{ageMin(checkedAt) > 15 ? " (stale)" : ""}</> : <>snapshot {SNAPSHOT}{error ? " · live: " + error : " · waiting for first SNMP poll"}</>}</div>
        </div>
        <span style={chip("#475569")} title="WatchGuard negotiates route SAs on demand; a route without an SA is idle, not down.">{active} / {total} routes with an SA (others idle)</span>
      </div>
      <div style={{ overflowX: "auto" }}><Diagram gws={G} /></div>
      <div style={{ marginTop: 12 }}>
        {G.map((g) => <GatewayBlock key={g.key} g={g} />)}
        <Interfaces />
      </div>
    </div>
  );
}
